import { useState } from 'react'
import { ISortOrderEnum } from 'types/graphqlTypes'

export interface IQuoteSortingOption {
  id: string
  label: string
  sortBy: string
  sortDirection: ISortOrderEnum
}

// The "id" values of these sorting options
// uniqely identify the sorting option object
// The "sortBy" value will be passed on by
// GraphQL directly to the provider for sorting.
// They need to exactly match the names of
// columns in the price_request table
const sortingOptions: IQuoteSortingOption[] = [
  {
    id: 'created_at_desc',
    label: 'Quote Created (Recent)',
    sortBy: 'created_at',
    sortDirection: ISortOrderEnum.DESC,
  },
  {
    id: 'created_at_asc',
    label: 'Quote Created (Oldest)',
    sortBy: 'created_at',
    sortDirection: ISortOrderEnum.ASC,
  },
  {
    id: 'pickup_date_asc',
    label: 'Pickup Date (Soonest)',
    sortBy: 'pickup_start_date',
    sortDirection: ISortOrderEnum.ASC,
  },
  {
    id: 'pickup_date_desc',
    label: 'Pickup Date (Latest)',
    sortBy: 'pickup_start_date',
    sortDirection: ISortOrderEnum.DESC,
  },
  {
    id: 'delivery_date_asc',
    label: 'Delivery Date (Soonest)',
    sortBy: 'delivery_start_date',
    sortDirection: ISortOrderEnum.ASC,
  },
  {
    id: 'delivery_date_desc',
    label: 'Delivery Date (Latest)',
    sortBy: 'delivery_start_date',
    sortDirection: ISortOrderEnum.DESC,
  },
  {
    id: 'customer_quote_desc',
    label: 'Quote Submitted (Highest)',
    sortBy: 'customer_quote',
    sortDirection: ISortOrderEnum.DESC,
  },
  {
    id: 'customer_quote_asc',
    label: 'Quote Submitted (Lowest)',
    sortBy: 'customer_quote',
    sortDirection: ISortOrderEnum.ASC,
  },
]

interface IUseQuoteSortingOptionsProps {
  setFocusedQuoteId: React.Dispatch<React.SetStateAction<string | null>>
}

interface IUseQuoteSortingOptionsReturn {
  sortBy: string
  sortDirection: ISortOrderEnum
  sortingOptions: IQuoteSortingOption[]
  onSortingOptionSelect: (id: string) => void
  defaultOptionLabel: string
}

export const useQuoteSortingOptions = ({
  setFocusedQuoteId,
}: IUseQuoteSortingOptionsProps): IUseQuoteSortingOptionsReturn => {
  // Sort by most recently created by default:
  const defaultOption: IQuoteSortingOption =
    sortingOptions.find(opt => opt.id === 'created_at_desc') || sortingOptions[0]
  const [sortBy, setSortBy] = useState<string>(defaultOption.sortBy)
  const [sortDirection, setSortDirection] = useState<ISortOrderEnum>(defaultOption.sortDirection)
  const defaultOptionLabel = defaultOption.label

  const onSortingOptionSelect = (id: string) => {
    setFocusedQuoteId(null)
    const option = sortingOptions.find(opt => opt.id === id)
    if (option) {
      setSortBy(option.sortBy)
      setSortDirection(option.sortDirection)
    }
  }

  return {
    defaultOptionLabel,
    onSortingOptionSelect,
    sortBy,
    sortDirection,
    sortingOptions,
  }
}
